/**
 * Login Screen - User authentication
 *
 * Allows users to login with email or username, and a password.
 * NOW THEME-AWARE
 */

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { login } from '../../services/authApi';
import { storeToken } from '../../services/tokenStorage';
import { useNavigation } from '@react-navigation/native';
import { showError, showSuccess } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage';
import { RequestError } from '../../utils/apiError';
import { validateRequired } from '../../utils/validation';

export function LoginScreen() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigation = useNavigation();
  const { setIsAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const getApiErrorMessage = useApiErrorMessage();

  // Create themed styles
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const handleLogin = async () => {
    const idValidation = validateRequired(identifier, t('auth.emailOrUsername'));
    if (!idValidation.isValid) {
      showError(idValidation.errorMessage!);
      return;
    }

    // Validate password
    const passwordValidation = validateRequired(password, t('auth.password'));
    if (!passwordValidation.isValid) {
      showError(passwordValidation.errorMessage!);
      return;
    }

    setLoading(true);
    try {
      const response = await login({ identifier, password });
      await storeToken(response.access_token);
      setIsAuthenticated(true);
      showSuccess(t('auth.loginSuccessful'));
    } catch (error) {
      // 403 email_not_verified: the account exists but never redeemed a signup
      // code. Route to code entry with the address prefilled instead of showing a
      // generic failure the user cannot act on. The finally block still runs, so
      // the spinner clears.
      if (error instanceof RequestError && error.code === 'email_not_verified') {
        (navigation.navigate as any)('VerifyEmail', { email: error.email ?? identifier });
        return;
      }
      // 429 rate_limited carries the server's cooldown: tell the user when
      // they may actually try again rather than a generic 'later'.
      if (
        error instanceof RequestError &&
        error.code === 'rate_limited' &&
        error.retryAfterSeconds !== undefined
      ) {
        showError(t('auth.loginRateLimited', { seconds: error.retryAfterSeconds }));
        return;
      }
      showError(getApiErrorMessage(error, 'auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('auth.welcomeBack')}</Text>
      <Text style={styles.subtitle}>{t('auth.signInToContinue')}</Text>

      <View style={styles.form}>
        <Input
          label={t('auth.emailOrUsername')}
          value={identifier}
          onChangeText={setIdentifier}
          placeholder={t('auth.enterYourEmailOrUsername')}
          autoCapitalize="none"
        />

        <Input
          label={t('auth.password')}
          value={password}
          onChangeText={setPassword}
          placeholder={t('auth.enterYourPassword')}
          secureTextEntry
        />

        <Button
          title={t('auth.login')}
          onPress={handleLogin}
          loading={loading}
          style={styles.loginButton}
        />

        <TouchableOpacity onPress={() => navigation.navigate('PasswordReset' as never)}>
          <Text style={styles.forgotLink}>{t('auth.forgotPassword')}</Text>
        </TouchableOpacity>

        <TouchableOpacity onPress={() => navigation.navigate('Register' as never)}>
          <Text style={styles.registerText}>
            {t('auth.dontHaveAccount')}{' '}
            <Text style={styles.registerLink}>{t('auth.registerLink')}</Text>
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      padding: 20,
      backgroundColor: theme.colors.background,
    },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      marginBottom: 8,
      textAlign: 'center',
      color: theme.colors.text,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    subtitle: {
      fontSize: 16,
      color: theme.colors.textSecondary,
      marginBottom: 32,
      textAlign: 'center',
    },
    form: {
      width: '100%',
    },
    loginButton: {
      marginTop: 16,
    },
    forgotLink: {
      marginTop: 16,
      textAlign: 'center',
      color: theme.colors.primary,
      fontWeight: '600',
    },
    registerText: {
      marginTop: 16,
      textAlign: 'center',
      color: theme.colors.textSecondary,
    },
    registerLink: {
      color: theme.colors.primary,
      fontWeight: '600',
    },
  });
}
